import { useMutation } from '@tanstack/react-query'
import { OpalApiRequestBody, OpalApiResponseDocument } from '../../../schema'
import { assetReferences as assetReferencesV1 } from '../../../v1/assetReferences'
import { assets } from '../../../v2/assets'
import { boards } from '../../../v3/boards'

export type CreatBoardMutationParams = {
  assetUuid?: string
  brandUuid: string
  description: string | null
  file?: File | null
  userUuid: string
  title: string
}

type BoardResponseDocument = OpalApiResponseDocument<'CreateBoardV3', 201>

type QueryParams = {
  onMutate?: (params: CreatBoardMutationParams) => void
  onSuccess?: (
    data: BoardResponseDocument,
    params: CreatBoardMutationParams
  ) => void
}

const buildRequestBody = ({
  brandUuid,
  description,
  assetUuid,
  title,
}: CreatBoardMutationParams): OpalApiRequestBody<'CreateBoardV3'> => ({
  data: {
    type: 'board',
    attributes: {
      title,
      description,
    },
    relationships: {
      asset_reference: {
        data: assetUuid
          ? {
              id: assetUuid,
              type: 'asset_reference',
            }
          : null,
      },
      workspace: {
        data: { type: 'workspace', id: brandUuid },
      },
    },
  },
})

export const useCreateBoardMutation = ({
  onMutate,
  onSuccess,
}: QueryParams) => {
  return useMutation({
    mutationFn: async (params: CreatBoardMutationParams) => {
      let assetReference
      if (params.file) {
        // Post to Asset Service
        const asset = await assets.create(params.file)
        // Create Asset Reference
        assetReference = await assetReferencesV1.create({
          requestBody: {
            data: {
              type: 'asset_reference',
              attributes: {
                asset_uuid: asset.data.id,
                filename: asset.data.attributes.file_name,
                filesize: asset.data.attributes.bytes,
                full_url: asset.data.attributes.url,
                height: asset.data.attributes.height ?? undefined,
                width: asset.data.attributes.width ?? undefined,
              },
              relationships: {},
            },
          },
        })
      }
      return await boards.create({
        requestBody: buildRequestBody({
          ...params,
          assetUuid: assetReference?.data.attributes.uuid,
        }),
      })
    },
    onMutate,
    onSuccess,
  })
}
